import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ChangeRolePayload,
  CreateGroupPayload,
  Group,
  InviteMemberPayload,
  RemoveMemberPayload,
  UpdateGroupPayload,
} from '../service/api';
import api from '../service/api';
import { createPersistStorage } from './utils/persist';

interface GroupState {
  group: Group | null;
  isLoading: boolean;
  error: string | null;
}

interface GroupActions {
  fetchGroup: () => Promise<void>;
  createGroup: (payload: CreateGroupPayload) => Promise<void>;
  updateGroup: (groupId: string, payload: UpdateGroupPayload) => Promise<void>;
  leaveGroup: () => Promise<void>;
  inviteMember: (payload: InviteMemberPayload) => Promise<void>;
  acceptInvitation: (code: string) => Promise<void>;
  removeMember: (payload: RemoveMemberPayload) => Promise<void>;
  changeRole: (payload: ChangeRolePayload) => Promise<void>;
  clearGroup: () => void;
}

type GroupStore = GroupState & GroupActions;

type GroupStorePersist = Pick<GroupStore, 'group'>;

const useGroupStore = create<GroupStore>()(
  persist<GroupStore, [], [], GroupStorePersist>(
    (set, get) => ({
      group: null,
      isLoading: false,
      error: null,

      fetchGroup: async () => {
        const hasCachedGroup = get().group !== null;
        set({ isLoading: !hasCachedGroup, error: null });
        try {
          const group = await api.groupGetCurrent();
          set({ group, isLoading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to load group';
          set({ error: errorMessage, isLoading: false });
        }
      },

      createGroup: async (payload: CreateGroupPayload) => {
        set({ isLoading: true, error: null });
        try {
          const group = await api.groupCreate(payload);
          set({ group, isLoading: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to create group';
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      updateGroup: async (groupId: string, payload: UpdateGroupPayload) => {
        try {
          const group = await api.groupUpdate(groupId, payload);
          set({ group });
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to update group');
        }
      },

      leaveGroup: async () => {
        try {
          await api.groupLeave();
          set({ group: null });
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to leave group');
        }
      },

      inviteMember: async (payload: InviteMemberPayload) => {
        try {
          await api.groupInviteMember(payload);
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to invite member');
        }
      },

      acceptInvitation: async (code: string) => {
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const result = await api.groupAcceptInvitation({ code, timezone });
          set({ group: result.group });
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to accept invitation');
        }
      },

      removeMember: async (payload: RemoveMemberPayload) => {
        try {
          await api.groupRemoveMember(payload);
          // Refresh group to get updated member list
          const group = await api.groupGetCurrent();
          set({ group });
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to remove member');
        }
      },

      changeRole: async (payload: ChangeRolePayload) => {
        try {
          const updatedGroup = await api.groupChangeRole(payload);
          set({ group: updatedGroup });
        } catch (error) {
          throw error instanceof Error
            ? error
            : new Error('Failed to change role');
        }
      },

      clearGroup: () => set({ group: null, isLoading: false, error: null }),
    }),
    {
      name: 'group-store',
      storage: createPersistStorage<GroupStorePersist>(),
      partialize: state => ({
        group: state.group,
      }),
    },
  ),
);

export default useGroupStore;
